import React, { useRef, useEffect, memo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import FreeSpinSceneTransitionController from "./FreeSpinSceneTransitionController";
import { safeInit } from "../../../../utilities";
import { useInitialIdleAnimations } from "../../../../Hooks/useLandingAnimations";
import { useFreeSpinControl } from "../../../../Contexts/FreeSpinControlContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";

import { selectFreeSpinTransitionActive } from "../../../../State/selectors";

const FreeSpinSceneTransition = () => {
  const dispatch = useDispatch();
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const { symbolWidth, symbolHeight, dimensions, dividerDimensions, device, orientation } =
    useDimensions();
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const { coverAllSymbolsDiagonal } = useFreeSpinControl();

  const freeSpinTransitionActive = useSelector(selectFreeSpinTransitionActive);
  const { freeSpinSceneTransitionRef } = useReelRefs();

  // Initialize controller only once
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new FreeSpinSceneTransitionController(
          containerRef.current,
          symbolWidth,
          symbolHeight,
          dimensions,
          dividerDimensions,
          device,
          orientation,
          dispatch
        );
        // Store controller ref for external access
        freeSpinSceneTransitionRef.current = controllerRef.current;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.resize(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
        device,
        orientation
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight]);

  useEffect(() => {
    if (freeSpinTransitionActive && controllerRef.current) {
      controllerRef.current.playAnimation(
        () => coverAllSymbolsDiagonal(),
        () => triggerInitialIdleAnimations(true),
      );
    } else if (!freeSpinTransitionActive && controllerRef.current) {
      controllerRef.current.stopTransition();
    }
  }, [freeSpinTransitionActive, triggerInitialIdleAnimations, coverAllSymbolsDiagonal]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
      }
    };
  }, []);

  return (
    <Container
      name="free_spin_scene_transition"
      x={0}
      y={0}
      zIndex={8}
      sortableChildren
      ref={containerRef}
    />
  );
};

export default memo(FreeSpinSceneTransition);
